import { useState, useEffect, type SetStateAction, type Dispatch } from 'react'
import { ChevronDown, Check } from 'lucide-react'
import { userAssignmentsService, type UserModuleAssignment } from '@/features/configuracion/api/userAssignmentsService'
import { apiService, type UserProfile } from '@/services/api'
import { logger } from '@/shared/lib/logger'

interface ModuleSelectorProps {
  user: UserProfile | null
  onModuleChange?: (moduleId: string) => void
  setShowModuleSelector: Dispatch<SetStateAction<boolean>>
}

// El backend guarda el módulo de proveedores como "Proveedores"; en este selector
// se muestra como "Compras" sin tocar el dato real ni las claves/permisos asociados.
const getModuleDisplayName = (name: string) => (name === 'Proveedores' ? 'Compras' : name)

const ModuleSelector = ({ user, onModuleChange, setShowModuleSelector }: ModuleSelectorProps) => {
  const [modules, setModules] = useState<UserModuleAssignment[]>([])
  const [selectedModule, setSelectedModule] = useState<string | null>(null)
  const [isOpen, setIsOpen] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (user?.id) {
      loadModules()
    }
  }, [user?.id, user?.currentModuleId])

  useEffect(() => {
    const handleModuleChanged = (event: Event) => {
      const customEvent = event as CustomEvent
      const { moduleId } = customEvent.detail
      setSelectedModule(moduleId)
    }

    window.addEventListener('module:changed', handleModuleChanged)
    return () => window.removeEventListener('module:changed', handleModuleChanged)
  }, [])

  const loadModules = async () => {
    if (!user?.id) return

    try {
      setLoading(true)
      const data = await userAssignmentsService.getUserModules(user.id)
      const activeModules = data.filter(m => m.isActive)
      setModules(activeModules)




      if (activeModules.length > 0 && !selectedModule) {
        setShowModuleSelector(true)
        const defaultModule = user?.currentModuleId || activeModules[0].moduleId
        setSelectedModule(defaultModule)
      } else {
        setShowModuleSelector(false)
      }
    } catch (error) {
      logger.error('module', '🔴 Error al cargar módulos:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleModuleChange = async (moduleId: string) => {
    try {

      setSelectedModule(moduleId)
      setIsOpen(false)

      // Llamar al endpoint para actualizar el módulo activo
      const updatedUser = await apiService.setActiveModule(moduleId)

      // Emitir evento con el usuario actualizado para que otros componentes se enteren del cambio
      window.dispatchEvent(new CustomEvent('module:changed', { detail: { moduleId, user: { currentModuleId: updatedUser.currentModuleId } } }))

      // Emitir evento para actualizar el perfil del usuario en useAuth
      window.dispatchEvent(new CustomEvent('user:profile:updated', { detail: { user: { currentModuleId: updatedUser.currentModuleId } } }))

      // Llamar callback si existe
      if (onModuleChange) {
        onModuleChange(moduleId)
      }
    } catch (error) {
      logger.error('module', '🔴 Error al cambiar módulo:', error)
      setSelectedModule(user?.currentModuleId || null)
    }
  }

  if (loading) {
    return (
      <div className="w-full bg-gray-100 border border-gray-200 rounded-lg px-4 py-3">
        <div className="flex flex-col items-start">
          <span className="text-xs text-gray-500 font-medium">Sistema</span>
          <span className="text-sm text-gray-400 mt-0.5">Cargando módulos...</span>
        </div>
      </div>
    )
  }

  if (modules.length === 0) {
    return (
      <div className="w-full bg-gray-100 border border-gray-200 rounded-lg px-4 py-3">
        <div className="flex flex-col items-start">
          <span className="text-xs text-gray-500 font-medium">Sistema</span>
          <span className="text-sm text-gray-400 mt-0.5">Sin módulos asignados</span>
        </div>
      </div>
    )
  }



  const selectedModuleName = getModuleDisplayName(modules.find(m => m.moduleId === selectedModule)?.module.name || 'Seleccionar módulo')

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full bg-white border border-gray-200 rounded-lg px-4 py-3 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <div className="flex flex-col items-start">
          <span className="text-xs text-gray-500 font-medium">Sistema</span>
          <span className="text-base font-semibold text-gray-900 mt-0.5">{selectedModuleName}</span>
        </div>
        <ChevronDown className={`w-5 h-5 text-gray-600 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute bottom-full left-0 right-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
            <div className="px-4 py-2 border-b border-gray-100">

              <span className="text-xs font-semibold text-gray-700">Sistema</span>

            </div>
            {modules.map((assignment) => {
              const isSelected = selectedModule === assignment.moduleId;

              return (
                <button
                  key={assignment.moduleId}
                  onClick={() => handleModuleChange(assignment.moduleId)}
                  className={`w-full px-4 py-3 flex items-center justify-between transition-colors ${isSelected
                    ? "bg-[#C3CBEB] text-[#232F61]"
                    : "text-gray-900"
                    }`}
                >
                  <span className={`text-sm font-medium ${isSelected ? "font-semibold" : ""}`}>
                    {getModuleDisplayName(assignment.module.name)}
                  </span>
                  {isSelected && (
                    <Check className="w-5 h-5 text-[#404DCE]" />
                  )}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  )
}

export default ModuleSelector
